import multiguard from 'vue-router-multiguard';
import { mapActions, mapGetters, mapState } from 'vuex';
import router from '../router';
import { generateIndex, rebindFormValidation, routerCheckFacilityUsageExists, routerCheckInitialStepComplete } from '../utils';

export default {
    name: 'FacilityUsageForm',
    template: '#facility-usage-form-template',
    beforeRouteEnter: multiguard([
        routerCheckInitialStepComplete,
        routerCheckFacilityUsageExists
    ]),
    props: ['isCreate', 'index'],
    data() {
        let selectedFacilityUsage = !this.isCreate ? this.$store.getters.getFacilityUsage(this.index) : null;

        return {
            areaId: this.isCreate ? '' : selectedFacilityUsage.areaId,
            facilityId: this.isCreate ? '' : selectedFacilityUsage.facilityId,
            activityId: this.isCreate ? '' : selectedFacilityUsage.activityId,
            clientDays: this.isCreate ? '' : selectedFacilityUsage.clientDays,
            currentIndex: !this.isCreate ? this.index : generateIndex()
        };
    },
    created() {
        if (!this.isCreate) {
            this.getAreas()
                .then(() => this.getFacilities(this.areaId))
                .then(this.getActivities)
                .then(this.doKenaiRiverSmaCheck)
                .then(() => {
                    $('#activity-upsert-form .areas-select').focus();
                });
        }
        else {
            this.resetFacilityFields();
            this.resetActivityFields();
            this.getAreas().then(() => {
                $('#activity-upsert-form .areas-select').focus();
            });
        }
    },
    updated() {
        rebindFormValidation();
    },
    computed: {
        ...mapState([
            'permitNumber',
            'permitYear',
            'areas',
            'areasLoading',
            'facilities',
            'facilitiesLoading',
            'activities',
            'activitiesLoading',
            'clientDaysHelpText'
        ]),
        ...mapGetters([
            'getActivity',
            'getArea',
            'getFacility'
        ]),
        areaIsKenaiRiverSma() {
            return this.areaId && this.selectedArea.name === 'Kenai' && this.facilityId && this.selectedFacility.name === 'Kenai River SMA';
        },
        areaIsWoodTikchik() {
            return this.areaId && this.selectedArea.name === 'Southwest' && this.facilityId && this.selectedFacility.name === 'Wood-Tikchik State Park';
        },
        filteredActivities() {
            const scenicGuidedTripsActivity = 'Scenic guided trips',
                kenaiRiverSmaActivities = [
                    'Scenic guided trips',
                    'Taxi/Shuttle Service'
                ],
                woodTikchikActivities = [
                    'Air Taxi',
                    'Bicycle/non-motorized vehicle rentals',
                    'Camping',
                    'Canoe/non-motorized watercraft rentals',
                    'Fishing',
                    'Guided Hunting/Transport-Outfitter',
                    'Guiding and transport not listed elsewhere',
                    'Hunting',
                    'Motorized watercraft rental',
                    'Scenic guided trips',
                    'Water Taxi',
                    'Watercraft tour'
                ];

            if (this.areaIsWoodTikchik) {
                return this.activities.filter(a => woodTikchikActivities.includes(a.description));
            }
            else if (this.areaIsKenaiRiverSma) {
                return this.activities.filter(a => kenaiRiverSmaActivities.includes(a.description));
            }

            return this.activities.filter(a => a.description !== scenicGuidedTripsActivity);
        },
        selectedActivity() {
            return this.getActivity(this.activityId);
        },
        selectedActivityFee() {
            return this.areaIsWoodTikchik && this.selectedActivity.description.indexOf('Hunting') === -1 ? 12 : this.selectedActivity.fee;
        },
        selectedArea() {
            return this.getArea(this.areaId);
        },
        selectedFacility() {
            return this.getFacility(this.facilityId);
        },
        totalActivityCost() {
            const errResult = '$0.00';

            if (!this.clientDays || !this.selectedActivity || !this.selectedActivityFee) {
                return errResult;
            }

            const result = this.clientDays * this.selectedActivityFee;

            if (isNaN(result) || result < 0) {
                return errResult;
            }

            if (result.toString().includes('.')) {
                return `$${result}`;
            }

            return `$${result}.00`;
        }
    },
    watch: {
        clientDays(newVal, oldVal) {
            if (!newVal) {
                return;
            }

            const parsedVal = parseInt(this.clientDays.toString().replace('.', '').replace('-', ''), 10);

            if (parsedVal > 100000) {
                this.clientDays = oldVal;
                return;
            }

            this.clientDays = newVal;
        }
    },
    methods: {
        ...mapActions([
            'getActivities',
            'getAreas',
            'getFacilities'
        ]),
        ...mapGetters([
            'getFacilityUsage',
            'indexOfFacilityUsage'
        ]),
        areaSelectChange(event) {
            this.resetFacilityFields();
            this.resetActivityFields();
            this.getFacilities(this.areaId).then(() => {
                $('#activity-upsert-form .facilities-select').focus();
            });
        },
        facilitySelectChange(event) {
            this.resetActivityFields();
            this.getActivities().then(this.doKenaiRiverSmaCheck);
        },
        doKenaiRiverSmaCheck() {
            if (this.areaIsKenaiRiverSma) {
                this.activityId = this.activities.find(a => a.description === 'Scenic guided trips').id;
                $('#activity-upsert-form .client-days-input').focus();
            }
            else {
                $('#activity-upsert-form .activities-select').focus();
            }
        },
        activitySelectChange(event) {
            this.clientDays = null;
            $('#activity-upsert-form .client-days-input').focus();
        },
        resetFacilityFields() {
            this.$store.dispatch('reloadFacilitiesFields');
            this.facilityId = '';
        },
        resetActivityFields() {
            this.$store.dispatch('reloadActivitiesFields');
            this.activityId = '';
            this.clientDays = null;
        },
        submitFacilityUsageForm(event) {
            if ($('#activity-upsert-form').valid()) {
                const result = {
                    areaId: this.areaId,
                    areaName: this.selectedArea.name,
                    facilityId: this.selectedFacility.id,
                    facilityName: this.selectedFacility.name,
                    activityId: this.selectedActivity.id,
                    activityName: this.selectedActivity.description,
                    clientDays: this.clientDays,
                    fee: this.selectedActivityFee,
                    feeBasis: this.selectedActivity.feeBasis,
                    index: this.currentIndex
                };

                if (this.isCreate) {
                    this.$store.dispatch('addFacilityUsage', result);
                }
                else {
                    const idx = this.indexOfFacilityUsage(result);
                    this.$store.dispatch('updateFacilityUsage', result);
                }

                router.push(`/${this.permitNumber}/${this.permitYear}`);
            }
        }
    }
};